/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import { Menu, Dropdown } from 'antd';
import moment from 'moment';
import React, { FC, useState, useEffect, useMemo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Timestamp } from 'api/models';
import { DueDatePicker } from 'components/atoms/due-date-picker';
import { queueMacrotask } from 'utils/macrotask';
import { ContentToRender } from './picker-title';
import styles from './range-picker.module.css';

interface Props {
  onDateChange: ([start, end]) => void;
  // RangePicker will return next 1/2 weeks instead of last 1/2 week if set to true
  isRangeForward?: boolean;
}

enum Ranges {
  One = 'One week',
  Two = 'Two weeks',
  Three = 'Three weeks',
  Four = 'Four weeks',
  Custom = 'Custom',
}

const rangeTranslationMap = {
  [Ranges.One]: 'picker.one_week',
  [Ranges.Two]: 'picker.two_weeks',
  [Ranges.Three]: 'picker.three_weeks',
  [Ranges.Four]: 'picker.four_weeks',
  [Ranges.Custom]: 'picker.custom',
};

const rangeDaysMap = {
  [Ranges.One]: 7,
  [Ranges.Two]: 14,
  [Ranges.Three]: 21,
  [Ranges.Four]: 28,
};
const getFromDate = (type: Ranges, isRangeForward) => {
  const momentObj = moment();
  const day = isRangeForward
    ? momentObj.add(rangeDaysMap[type], 'day')
    : momentObj.subtract(rangeDaysMap[type], 'day');

  return day.endOf('day').valueOf();
};

type DateRange = [Timestamp | null, Timestamp | null];

export const RangePicker: FC<Props> = ({ onDateChange, isRangeForward }) => {
  const today = moment().endOf('day');
  const lastTwoWeeks = moment().subtract(14, 'day').endOf('day');
  const nextTwoWeeks = moment().add(14, 'day').endOf('day');

  const initialDates = isRangeForward
    ? [today.valueOf(), nextTwoWeeks.valueOf()]
    : [lastTwoWeeks.valueOf(), today.valueOf()];
  const [date, setDate] = useState<DateRange>(initialDates as DateRange);

  const [rangeType, setRangeType] = useState<Ranges>(Ranges.Two);
  const [isDropdownVisible, setDropdownVisibility] = useState<boolean>(false);
  const [isRangePickerVisible, setRangePickerVisibility] =
    useState<boolean>(false);

  const handleMenuItemClick = useCallback(
    (e) => {
      if (e.key === Ranges.Custom) {
        return;
      }

      queueMacrotask(() => {
        setRangeType(e.key);
        const newDate = getFromDate(e.key, isRangeForward);
        const newDateRange = isRangeForward
          ? [date[0], newDate]
          : [newDate, date[1]];

        setDate(newDateRange as DateRange);
        setDropdownVisibility(false);
      });
    },
    [date, isRangeForward],
  );

  useEffect(() => {
    const tod = moment().endOf('day');
    const last2Weeks = moment().subtract(14, 'day').endOf('day');
    const next2Weeks = moment().add(14, 'day').endOf('day');
    const newDateRange = isRangeForward
      ? [tod.valueOf(), next2Weeks.valueOf()]
      : [last2Weeks.valueOf(), tod.valueOf()];

    setDate(newDateRange as DateRange);
  }, [isRangeForward]);

  useEffect(() => {
    onDateChange(date);
  }, [date, onDateChange]);

  const onDatePickerVisibilityChange = (val) => {
    setRangePickerVisibility(val);
  };

  const menu = useMemo(
    () => (
      <Menu onClick={handleMenuItemClick}>
        <Menu.Item key={Ranges.One}>
          <FormattedMessage id={rangeTranslationMap[Ranges.One]} />
        </Menu.Item>
        <Menu.Item key={Ranges.Two}>
          <FormattedMessage id={rangeTranslationMap[Ranges.Two]} />
        </Menu.Item>
        <Menu.Item key={Ranges.Three}>
          <FormattedMessage id={rangeTranslationMap[Ranges.Three]} />
        </Menu.Item>
        <Menu.Item key={Ranges.Four}>
          <FormattedMessage id={rangeTranslationMap[Ranges.Four]} />
        </Menu.Item>
        <DueDatePicker
          overlayClassName={styles.range_picker}
          block={false}
          size="small"
          startDate={date[0]}
          onDatePickerVisibilityChange={onDatePickerVisibilityChange}
          hideFooter
          isRangePicker
          dueDate={date[1]}
          onDateChange={([s, d]) => {
            setDate([s, d]);
            setRangeType(Ranges.Custom);
          }}
          renderTrigger={(togglePickerVisibility) => {
            // Since DueDatePicker using popover to position and have its own internal state
            // to show the pickers, we're rendering the trigger here.
            return (
              <div
                onClick={togglePickerVisibility}
                className="ant-dropdown-menu-item"
              >
                <FormattedMessage id={rangeTranslationMap[Ranges.Custom]} />
              </div>
            );
          }}
        />
      </Menu>
    ),
    [date, handleMenuItemClick],
  );

  return (
    <Dropdown
      overlay={menu}
      trigger={['click']}
      visible={isDropdownVisible}
      onVisibleChange={(isVisible) => {
        if (!isRangePickerVisible) {
          setDropdownVisibility(isVisible);
        }
      }}
    >
      <span className={styles.title}>
        {rangeType === Ranges.Custom ? (
          <ContentToRender
            startDate={moment(date[0])}
            dueDate={moment(date[1])}
          />
        ) : (
          <>
            <FormattedMessage id={rangeTranslationMap[rangeType]} />
            <ChevronDown16 />
          </>
        )}
      </span>
    </Dropdown>
  );
};
